<template>
  <div
    class="d-flex flex-row fill-width">
    <v-card
      class="invoice-parameter-card px-5 pt-5 pb-10">
      <TextBase textLg>
        Detalhes da Regra
      </TextBase>
      <TextBase>
        Revise os dados e ajuste a regra, se necessário.
      </TextBase>
      <v-card
        class="invoice-parameter-card px-0 pb-4">
        <v-data-table
          hide-default-footer
          mobile-breakpoint="0"
          class="invoice-rule-detail-table"
          :items-per-page="-1"
          :headers="headers"
          :item-key="'tempId'"
          :items="configs">
          <template
            v-slot:[`header.rule`]>
            <HeaderForm>Regra</HeaderForm>
          </template>
          <template
            v-slot:[`header.description`]>
            <HeaderForm>Descrição</HeaderForm>
          </template>
          <template
            v-slot:[`header.value`]>
            <HeaderForm>Valor</HeaderForm>
          </template>
          <template
            v-slot:[`header.status`]>
            <HeaderForm>Status da Regra</HeaderForm>
          </template>
          <template
            v-slot:[`header.action`]>
            <HeaderForm>Ação</HeaderForm>
          </template>

          <template
            v-slot:[`item.rule`]="{ item }">
            <Autocomplete
              label="Regra"
              :value="item.ruleId"
              :items="ruleTypes"
              item-text="name"
              item-value="id"
              :rules="[rule.hasDuplicatesInTheList('ruleId', item.ruleId, configs), rule.required]"
              :disabled="isUpdatingConfig && !item.new"
              @change="updateRule(item.tempId, $event)"
              v-bind="autocompleteAttrs"
              :style="unlinkOrInactiveStyle(item)"
            />
          </template>
          <template
            v-slot:[`item.description`]="{ item }">
            <Textfield
              label="Descrição"
              v-bind="textFieldAttrs"
              :value="item.description"
              readonly
              showTooltip
              :tooltipValue="item.description"
              :style="unlinkOrInactiveStyle(item)"
            />
          </template>
          <template
            v-slot:[`item.value`]="{ item }">
            <Input
              v-if="isText(item)"
              label="Valor"
              :disabled="!item.ruleId"
              :rules="isText(item) ? [rule.required] : []"
              v-mask="textMask(item.valueType)"
              @input="onInput(item.tempId, $event)"
              :type="item.valueType"
              v-model="configValues[item.tempId]"
              :style="unlinkOrInactiveStyle(item)"
            />
            <Input
              v-if="isBoolean(item)"
              label="Valor"
              :disabled="!item.ruleId"
              :rules="isBoolean(item) ? [rule.isBoolean] : []"
              @change="onChange(item.tempId, $event)"
              :type="item.valueType"
              :value="configValues[item.tempId]"
              :style="unlinkOrInactiveStyle(item)"
            />
            <Input
              v-if="isList(item)"
              label="Valor"
              :disabled="!item.ruleId"
              :rules="isList(item) ? [rule.requiredArray] : []"
              item-text="description"
              item-value="key"
              multiple
              @change="onChange(item.tempId, $event)"
              :items="item.possibleValues"
              :type="item.valueType"
              :value="configValues[item.tempId]"
              :style="unlinkOrInactiveStyle(item)"
            />
          </template>
          <template
            v-slot:[`item.status`]="{ item }">
            <v-switch
              :disabled="!item.ruleId"
              :class="{
                'mx-0 mt-0 mb-6': true,
                'font-weight-bold': item.active
              }"
              :input-value="item.active"
              :label="item.active ? 'Ativa' : 'Inatívo'"
              @change="startUpdateStatus(item, $event)"
              hide-details
            />
          </template>
          <template
            v-slot:[`item.action`]="{ item }">
            <div class="d-flex flex-row align-center justify-center mb-6">
              <v-tooltip
                bottom
                v-if="item.new === false && item.unlink === false">
                <template v-slot:activator="{on, attrs}">
                  <v-btn
                    @click="startUnlinkConfig(item, true)"
                    icon
                    class="elevation-0"
                    v-on="on"
                    v-bind="attrs">
                    <v-img
                      height="16"
                      contain
                      src="icons/unlink.svg"
                    />
                  </v-btn>
                </template>
                <span>
                  Desvincular
                </span>
              </v-tooltip>
              <v-tooltip
                bottom
                v-if="item.new === false && item.unlink === true">
                <template v-slot:activator="{on, attrs}">
                  <v-btn
                    @click="startUnlinkConfig(item, false)"
                    icon
                    class="elevation-0"
                    v-on="on"
                    v-bind="attrs">
                    <v-img
                      height="16"
                      contain
                      src="icons/link.svg"
                    />
                  </v-btn>
                </template>
                <span>
                  Vincular
                </span>
              </v-tooltip>
              <v-tooltip
                bottom
                v-if="configs.length > 1 && item.new">
                <template v-slot:activator="{on, attrs}">
                  <v-btn
                    icon
                    class="elevation-0"
                    v-on="on"
                    v-bind="attrs">
                    <v-icon
                      :disabled="configs.length === 1"
                      @click="startRemoveConfig(item.tempId)"
                      icon
                      class="elevation-0"
                      v-on="on"
                      v-bind="attrs"
                      color="error"
                      size="16">
                      fas fa-trash
                    </v-icon>
                  </v-btn>
                </template>
                <span>
                  Remover linha
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
        <v-btn
          class="action-button-modal foreground-accent--text ml-5"
          color="primary"
          @click="addConfig"
          v-if="isCreatingConfig || isUpdatingConfig || isCopyingConfig">
          <v-icon color="foreground-accent">
            fas fa-plus
          </v-icon>
          <span
            class="ml-2">
            Adicionar Regra
          </span>
        </v-btn>
      </v-card>
    </v-card>
  </div>
</template>
<script>
import InvoiceParametersMixin from '@/shared/mixins/invoiceParameters/InvoiceParametersMixin';
import InvoiceParameterHeaderForm from './components/InvoiceParameterHeaderForm.vue';
import InvoiceParameterInput from './components/InvoiceParameterInput.vue';
import InvoiceParameterText from './components/InvoiceParameterText.vue';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import Autocomplete from '../Default/Autocomplete.vue';
import Textfield from '../Default/Textfield.vue';
export default {
  name: "InvoiceRuleDetail",
  components: {
    Autocomplete,
    Textfield,
    HeaderForm: InvoiceParameterHeaderForm,
    TextBase: InvoiceParameterText,
    Input: InvoiceParameterInput,
  },
  data: () => ({
    configValues: {},
  }),
  mixins: [
    InvoiceParametersMixin,
  ],
  watch: {
    configs: {
      deep: true,
      immediate: true,
      handler(formConfigs) {
        this.configValues = {};
        if (Array.isArray(formConfigs) && formConfigs.length > 0) {
          formConfigs.forEach((config) => {
            this.$set(this.configValues, config.tempId, config.value);
          });
        }
      }
    }
  },
  methods: {
    ...mapActions({
      getRuleData: 'invoice-parameter/getRuleData',
    }),
    ...mapMutations({
      setShowRuleStatusInactiveModal: 'invoice-parameter/setShowRuleStatusInactiveModal',
      setShowRuleStatusActiveModal: 'invoice-parameter/setShowRuleStatusActiveModal',
      setShowRuleUnlinkModal: 'invoice-parameter/setShowRuleUnlinkModal',
      setShowRuleLinkModal: 'invoice-parameter/setShowRuleLinkModal',
      updateConfig: 'invoice-parameter/updateConfig',
      removeConfig: 'invoice-parameter/removeConfig',
      addConfig: 'invoice-parameter/addConfig',
    }),
    textMask(valueType) {
      if (valueType === 'INTEGER') {
        return createNumberMask({
          prefix: '',
          suffix: '',
          includeThousandsSeparator: false,
          allowNegative: true,
        });

      } else if (valueType === 'DECIMAL') {
        return createNumberMask({
          prefix: '',
          suffix: '',
          allowDecimal: true,
          allowNegative: true,
          includeThousandsSeparator: false,
          decimalSymbol: ',',
          decimalLimit: 2,
        });
      } else {
        return '';
      }
    },
    onInput(tempId, newValue) {
      const configIndex = this.configs.findIndex((c) => c.tempId === tempId);
      const currentValue = this.configs[configIndex].value;
      if (this.areEqualDeep(currentValue, newValue)) return;
      this.updateConfig({
        tempId: tempId,
        config: { value: newValue },
      });
    },
    onChange(tempId, newValue) {
      const currentValue = this.configValues[tempId];
      if (this.areEqualDeep(currentValue, newValue)) return;
      this.updateConfig({
        tempId: tempId,
        config: {
          value: newValue,
        }
      });
    },
    updateRule(tempId, ruleId) {
      if (!ruleId) {
        this.configValues[tempId] = null;
        const payload = {
          tempId: tempId,
          config: {
            ruleId: null,
            valueType: null,
            description: null,
            observation: null,
            possibleValues: [],
            value: null,
          }
        }
        this.updateConfig(payload);
        return;
      }
      const ruleType = this.ruleTypes.find(rule => rule.id === ruleId);
      const valueType = ruleType.valueType;
      const isList = !["STRING", "INTEGER", "DECIMAL", "BOOLEAN"].includes(valueType);
      const valueOption = isList ? this.valueTypes.find(vt => vt.key === valueType) : false;
      const possibleValues = valueOption && valueOption.options && valueOption.options.length > 0 ? valueOption.options : [];
      this.configValues[tempId] = null;

      const payload = {
        tempId: tempId,
        config: {
          ruleId: ruleId,
          valueType: valueType,
          description: ruleType ? ruleType.description : '',
          observation: ruleType ? ruleType.observation : '',
          possibleValues: possibleValues,
          value: null,
        }
      }

      this.updateConfig(payload);
    },
    startUpdateStatus(item, active) {
      const payload = {
        tempId: item.tempId,
        config: {
          active: active
        }
      }
      this.updateConfig(payload);
      if (item.new) return;
      if (!active) {
        this.setShowRuleStatusInactiveModal({show: true, configInValidation: payload});
        return;
      }
      this.setShowRuleStatusActiveModal({show: true, configInValidation: payload});
    },
    async startUnlinkConfig(item, unlinking) {
      const payload = {
        tempId: item.tempId,
        config: {
          unlink: unlinking
        }
      }
      this.updateConfig(payload);
      if (!unlinking) {
        this.setShowRuleLinkModal({show: true, configInValidation: payload});
        return;
      }
      this.setShowRuleUnlinkModal({show: true, configInValidation: payload});
    },
    startRemoveConfig(tempId) {
      this.removeConfig(tempId);
    },
    isText(item) {
      return ['STRING', 'INTEGER', 'DECIMAL'].includes(item.valueType)
    },
    isBoolean(item) {
      return item.valueType === 'BOOLEAN';
    },
    isList(item) {
      return !this.isText(item) && !this.isBoolean(item);
    },
    unlinkOrInactiveStyle(item) {
      return item.unlink || !item.active ? 'opacity: 30%' : ''
    }
  },
  computed: {
    ...mapGetters({
      isUpdatingConfig: 'invoice-parameter/isUpdatingConfig',
      isCreatingConfig: 'invoice-parameter/isCreatingConfig',
      isCopyingConfig: 'invoice-parameter/isCopyingConfig',
      valueTypes: 'invoice-parameter/valueTypes',
      ruleTypes: 'invoice-parameter/ruleTypes',
      configs: 'invoice-parameter/formConfigs',
    }),
    headers() {
      return [
        { value: 'rule', sortable: false },
        { value: 'description', sortable: false },
        { value: 'value', sortable: false },
        { value: 'status', sortable: false },
        { value: 'action', width: '60px', sortable: false, align: 'center' },
      ]
    },
  },
  async created() {
    await this.getRuleData();
  },
}
</script>
<style lang="scss">
@import "@/shared/styles/invoiceParameters/table.scss";
</style>